import { useEffect, useState } from "react";

import { api, ApiError } from "../lib/api";
import { getUser, updateUser, type AdminUser } from "../lib/auth";
import { AlertDialog } from "../components/ConfirmDialog";

/**
 * 个人信息（任意登录管理员）：改显示名 + 改密码。
 * 显示名改后同步更新本地缓存，侧边栏即时反映。
 */
type Me = { id: number; email: string; display_name: string | null; role: "admin" | "editor"; last_login_at: string | null };

export function ProfilePage() {
  const [me, setMe] = useState<Me | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);

  useEffect(() => {
    api.get<Me>("/api/admin/auth/me").then(setMe).catch(() => {});
  }, []);

  if (!me) return <div className="muted" style={{ fontSize: 13 }}>加载中…</div>;

  return (
    <div>
      <div className="page-header">
        <div><div className="page-title">个人信息</div></div>
      </div>

      <div style={{ display: "grid", gap: 16, maxWidth: 420 }}>
        <ProfileCard me={me} onSaved={(u) => { setMe({ ...me, display_name: u.display_name }); setOkMsg("显示名已更新。"); }} />
        <PasswordCard onSaved={() => setOkMsg("密码已修改，下次登录请使用新密码。")} />
      </div>

      <AlertDialog open={okMsg !== null} title="操作成功" message={okMsg} onClose={() => setOkMsg(null)} />
    </div>
  );
}

// 基本信息：邮箱/角色只读，显示名可改
function ProfileCard({ me, onSaved }: { me: Me; onSaved: (u: AdminUser) => void }) {
  const [name, setName] = useState(me.display_name ?? "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    setBusy(true); setErr(null);
    try {
      const u = await api.patch<AdminUser>("/api/admin/auth/me", { display_name: name.trim() });
      const cur = getUser();
      if (cur) updateUser({ ...cur, display_name: u.display_name }); // 同步侧边栏显示
      onSaved(u);
    } catch (e) { setErr(e instanceof ApiError ? `保存失败 ${e.status}` : String(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="form">
      <div className="ce-label" style={{ marginBottom: 12 }}>基本信息</div>
      <label className="ce-label">邮箱</label>
      <input value={me.email} disabled style={{ width: "100%", marginBottom: 10, opacity: 0.6 }} />
      <label className="ce-label">角色</label>
      <input value={me.role === "admin" ? "超级管理员" : "编辑"} disabled style={{ width: "100%", marginBottom: 10, opacity: 0.6 }} />
      <label className="ce-label">显示名</label>
      <input value={name} onChange={(e) => setName(e.target.value)} placeholder="显示名" maxLength={50} style={{ width: "100%", marginBottom: 12 }} />
      {err && <div className="muted" style={{ color: "#b04a3a", fontSize: 12, marginBottom: 8 }}>{err}</div>}
      <button className="btn btn-primary" style={{ width: "100%" }} disabled={busy} onClick={save}>{busy ? "保存中…" : "保存"}</button>
    </div>
  );
}

// 修改密码：旧密码 + 新密码 + 确认
function PasswordCard({ onSaved }: { onSaved: () => void }) {
  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    setErr(null);
    if (newPw.length < 6) { setErr("新密码至少 6 位"); return; }
    if (newPw !== confirmPw) { setErr("两次输入的新密码不一致"); return; }
    setBusy(true);
    try {
      await api.post("/api/admin/auth/me/password", { old_password: oldPw, new_password: newPw });
      setOldPw(""); setNewPw(""); setConfirmPw("");
      onSaved();
    } catch (e) {
      if (e instanceof ApiError && e.status === 400) setErr("旧密码不正确");
      else setErr(e instanceof ApiError ? `修改失败 ${e.status}` : String(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="form">
      <div className="ce-label" style={{ marginBottom: 12 }}>修改密码</div>
      <label className="ce-label">当前密码</label>
      <input type="password" value={oldPw} onChange={(e) => setOldPw(e.target.value)} autoComplete="current-password" style={{ width: "100%", marginBottom: 10 }} />
      <label className="ce-label">新密码</label>
      <input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} autoComplete="new-password" placeholder="至少 6 位" style={{ width: "100%", marginBottom: 10 }} />
      <label className="ce-label">确认新密码</label>
      <input type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} autoComplete="new-password" style={{ width: "100%", marginBottom: 12 }} />
      {err && <div className="muted" style={{ color: "#b04a3a", fontSize: 12, marginBottom: 8 }}>{err}</div>}
      <button className="btn btn-primary" style={{ width: "100%" }} disabled={busy || !oldPw || !newPw} onClick={save}>{busy ? "提交中…" : "修改密码"}</button>
    </div>
  );
}
